import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Vehiculo } from '../../../hooks/useSettings';

interface Props {
  vehiculo: Vehiculo | null;
  onClose: () => void;
}

const VehicleDetailModal = ({ vehiculo: v, onClose }: Props) => {
  const [activeImg, setActiveImg] = useState(0);

  const allImages = v
    ? [v.imagen, ...(v.imagenes ?? [])].filter(Boolean)
    : [];

  useEffect(() => {
    setActiveImg(0);
  }, [v]);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setActiveImg((i) => (i + 1) % allImages.length);
      if (e.key === 'ArrowLeft') setActiveImg((i) => (i - 1 + allImages.length) % allImages.length);
    },
    [onClose, allImages.length],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  if (!v) return null;

  const hasOffer = v.oferta_activa && v.precio_oferta && v.precio_oferta > 0;
  const finalPrice = hasOffer ? v.precio_oferta! : v.precio;

  const specs = [
    { icon: 'ri-calendar-line', label: 'Año', value: v.anio },
    { icon: 'ri-road-map-line', label: 'Kilómetros', value: v.km !== undefined ? `${v.km.toLocaleString('es-ES')} km` : null },
    { icon: 'ri-gas-station-line', label: 'Combustible', value: v.combustible },
    { icon: 'ri-settings-3-line', label: 'Transmisión', value: v.transmision },
    { icon: 'ri-flashlight-line', label: 'Potencia', value: v.potencia ? `${v.potencia} CV` : null },
    { icon: 'ri-palette-line', label: 'Color', value: v.color },
    { icon: 'ri-door-open-line', label: 'Puertas', value: v.puertas },
    { icon: 'ri-user-line', label: 'Plazas', value: v.plazas },
  ].filter((s) => s.value !== null && s.value !== undefined && s.value !== '');

  const whatsappMsg = encodeURIComponent(
    `Hola! Me interesa el vehículo: ${v.nombre}. ¿Podéis darme más información?`,
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-5xl max-h-[92vh] overflow-y-auto bg-zinc-950 border border-zinc-800"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-all cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="flex flex-col lg:flex-row">
          {/* Left: Gallery */}
          <div className="lg:w-[55%] flex-shrink-0">
            {/* Main image */}
            <div className="relative h-72 lg:h-[420px] bg-zinc-900 overflow-hidden">
              {allImages.length > 0 ? (
                <img
                  src={allImages[activeImg]}
                  alt={v.nombre}
                  className="w-full h-full object-cover object-top transition-opacity duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-700">
                  <i className={`${v.tipo === 'moto' ? 'ri-motorbike-line' : 'ri-car-line'} text-7xl`} />
                </div>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-1.5 flex-wrap">
                {hasOffer && (
                  <span className="text-xs font-black uppercase px-2.5 py-1 bg-red-500 text-white">
                    Oferta
                  </span>
                )}
                {!v.disponible && (
                  <span className="text-xs font-black uppercase px-2.5 py-1 bg-zinc-700 text-zinc-300">
                    Reservado
                  </span>
                )}
                <span className="text-[10px] font-bold uppercase px-2.5 py-1 bg-black/70 text-zinc-300 capitalize">
                  {v.tipo}
                </span>
              </div>

              {/* Nav arrows */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={() => setActiveImg((i) => (i - 1 + allImages.length) % allImages.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/70 text-white hover:bg-black transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-xl" />
                  </button>
                  <button
                    onClick={() => setActiveImg((i) => (i + 1) % allImages.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/70 text-white hover:bg-black transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-xl" />
                  </button>
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[10px] font-bold px-2 py-1">
                    {activeImg + 1} / {allImages.length}
                  </div>
                </>
              )}
            </div>

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-1.5 p-3 bg-zinc-900 overflow-x-auto">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(idx)}
                    className={`flex-shrink-0 w-16 h-12 overflow-hidden border-2 transition-all cursor-pointer ${
                      activeImg === idx ? 'border-yellow-400' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Info */}
          <div className="lg:w-[45%] p-6 lg:p-8 flex flex-col">
            <h2 className="text-white font-black text-xl lg:text-2xl leading-tight mb-2">
              {v.nombre}
            </h2>

            {/* Price */}
            <div className="mb-5">
              {hasOffer ? (
                <div className="flex items-baseline gap-3">
                  <span className="text-3xl font-black text-yellow-400">
                    {Number(finalPrice).toLocaleString('es-ES')} €
                  </span>
                  <span className="text-zinc-500 text-base line-through">
                    {Number(v.precio).toLocaleString('es-ES')} €
                  </span>
                  <span className="text-xs font-black bg-red-500 text-white px-2 py-0.5">
                    -{Math.round((1 - finalPrice! / v.precio) * 100)}%
                  </span>
                </div>
              ) : v.tipo === 'alquiler' ? (
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-black text-yellow-400">
                    {v.precio_dia ?? '—'} €
                  </span>
                  <span className="text-zinc-500 text-sm">/día</span>
                </div>
              ) : (
                <span className="text-3xl font-black text-white">
                  {Number(v.precio).toLocaleString('es-ES')} €
                </span>
              )}
            </div>

            {/* Specs grid */}
            {specs.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-5">
                {specs.map((s) => (
                  <div key={s.label} className="bg-zinc-900 border border-zinc-800 px-3 py-2.5">
                    <div className="flex items-center gap-1.5 text-zinc-600 text-[10px] font-bold uppercase tracking-wider mb-0.5">
                      <i className={`${s.icon} text-xs`} />
                      {s.label}
                    </div>
                    <div className="text-white text-sm font-bold">{String(s.value)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Description */}
            {v.descripcion && (
              <div className="mb-5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1.5">
                  Descripción
                </p>
                <p className="text-zinc-400 text-sm leading-relaxed">{v.descripcion}</p>
              </div>
            )}

            {/* CTAs */}
            <div className="mt-auto space-y-2.5 pt-4 border-t border-zinc-800">
              <a
                href={`https://wa.me/34666365922?text=${whatsappMsg}`}
                target="_blank"
                rel="nofollow noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-black text-xs uppercase tracking-widest px-6 py-3.5 hover:bg-yellow-300 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-base" />
                Consultar por WhatsApp
              </a>
              <Link
                to="/contacto"
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 bg-zinc-900 border border-zinc-700 text-white font-bold text-xs uppercase tracking-widest px-6 py-3 hover:border-zinc-500 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line" />
                Solicitar información
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailModal;
